/**
 * TechTimeline.jsx · v3.3.1 · Timeline compacto por técnico del día.
 * ─────────────────────────────────────────────────────────────────────
 * Renderiza una barra horaria 08:00-20:00 por técnico con sus bloques
 * ocupados. Los huecos (>= 30 min) quedan visibles para que el coordinador
 * identifique dónde puede caer una nueva OT del carril "Cola de Despacho".
 *
 * Ecuador timezone (UTC-5) usada en render.
 */
import { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { Loader2, CalendarClock, User } from "lucide-react";

const WINDOW_START_H = 8;   // 08:00
const WINDOW_END_H = 20;    // 20:00
const WINDOW_MIN = (WINDOW_END_H - WINDOW_START_H) * 60;

export default function TechTimeline({ date, onSlotClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const iso = date || new Date().toLocaleDateString("en-CA", { timeZone: "America/Guayaquil" });

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/v1/ot_smart/timeline-day", { params: { date: iso } })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [iso]);

  if (loading) return (
    <div className="p-4 flex items-center gap-2 text-xs text-gray-500" data-testid="tech-timeline-loading">
      <Loader2 size={12} className="animate-spin"/> Cargando timeline de técnicos…
    </div>
  );
  if (!data || !data.techs?.length) return (
    <div className="p-4 text-center text-xs text-gray-400 italic" data-testid="tech-timeline-empty">
      Sin técnicos con agenda hoy.
    </div>
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3" data-testid="tech-timeline">
      <div className="flex items-center gap-2 mb-3 pb-2 border-b">
        <CalendarClock size={14} style={{ color: "#0A3747" }} />
        <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: "#0A3747" }}>
          Timeline de Despacho · {iso}
        </div>
        <div className="text-[10px] text-gray-500 font-mono ml-2">
          {data.techs_count} técnicos · buffer {data.buffer_min ?? 30}min · UTC−5
        </div>
        <div className="flex-1"/>
        <div className="flex items-center gap-3 text-[9px] text-gray-500 font-mono">
          <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded bg-[#0A3747]"/> Ocupado</span>
          <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded" style={{ background: "#E9EE6E" }}/> Buffer 30min</span>
          <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded bg-gray-100 border border-gray-300"/> Hueco libre</span>
        </div>
      </div>

      {/* Regla horaria */}
      <div className="flex gap-2 items-center mb-1 pl-32">
        <div className="flex-1 flex text-[9px] text-gray-400 font-mono">
          {Array.from({ length: WINDOW_END_H - WINDOW_START_H + 1 }, (_, i) => (
            <div key={i} style={{ flex: "1 1 0" }} className="text-left border-l border-gray-100 pl-0.5">
              {String(WINDOW_START_H + i).padStart(2, "0")}
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-1.5 max-h-72 overflow-y-auto">
        {data.techs.map((tech) => (
          <TechRow key={tech.id} tech={tech} iso={iso} onSlotClick={onSlotClick} />
        ))}
      </div>
    </div>
  );
}

function TechRow({ tech, iso, onSlotClick }) {
  const { blocks, freeMin } = useMemo(() => buildRow(tech.slots || [], iso), [tech.slots, iso]);
  return (
    <div className="flex items-center gap-2 group" data-testid={`tech-row-${tech.id}`}>
      <div className="w-32 flex items-center gap-1.5 flex-shrink-0">
        <User size={11} className="text-gray-400" />
        <span className="text-[11px] font-semibold text-gray-800 truncate" title={tech.name}>
          {shortName(tech.name)}
        </span>
      </div>
      <div className="flex-1 h-5 relative bg-gray-50 border border-gray-200 rounded overflow-hidden">
        {blocks.map((b, i) => (
          <div
            key={i}
            title={b.title}
            onClick={() => b.type === "busy" && onSlotClick?.({ tech, slot: b.raw })}
            className={
              b.type === "busy"
                ? "absolute top-0 bottom-0 cursor-pointer hover:opacity-80 flex items-center justify-center"
                : b.type === "buffer"
                ? "absolute top-0 bottom-0 opacity-70"
                : "absolute top-0 bottom-0"
            }
            style={{
              left: `${b.leftPct}%`,
              width: `${b.widthPct}%`,
              background: b.type === "busy" ? "#0A3747"
                        : b.type === "buffer" ? "#E9EE6E"
                        : "transparent",
              borderRight: b.type === "busy" ? "1px solid #FBFCE0" : undefined,
            }}
            data-testid={b.type === "busy" ? `slot-busy-${tech.id}-${i}` : undefined}
          >
            {b.type === "busy" && b.widthPct > 4 && (
              <span className="text-[8px] font-bold" style={{ color: "#FBFCE0" }}>
                {b.label}
              </span>
            )}
          </div>
        ))}
      </div>
      <div className="w-16 text-right flex-shrink-0">
        <span className={`text-[10px] font-mono font-bold ${freeMin >= 120 ? "text-emerald-700" : freeMin >= 60 ? "text-amber-700" : "text-red-600"}`}
              title={`${freeMin} min libres en ventana 08:00-20:00`}>
          {Math.round(freeMin / 60 * 10) / 10}h libres
        </span>
      </div>
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────────────
function buildRow(slots, iso) {
  const BUFFER_MIN = 30;
  const parsed = slots
    .map((s) => {
      const start = s.start ? new Date(s.start) : null;
      const end = s.end ? new Date(s.end) : null;
      return { start, end, raw: s };
    })
    .filter(({ start, end }) => start && end && !isNaN(start) && !isNaN(end))
    .sort((a, b) => a.start - b.start);

  const blocks = [];
  let usedMin = 0;
  parsed.forEach((p) => {
    const leftMin = clampToWindow(p.start, iso);
    const rightMin = clampToWindow(p.end, iso);
    if (rightMin <= 0 || leftMin >= WINDOW_MIN) return; // fuera de ventana
    const b0 = Math.max(0, leftMin);
    const b1 = Math.min(WINDOW_MIN, rightMin);
    usedMin += Math.max(0, b1 - b0);
    blocks.push({
      type: "busy",
      leftPct: (b0 / WINDOW_MIN) * 100,
      widthPct: ((b1 - b0) / WINDOW_MIN) * 100,
      label: p.raw.afc_code || `#${p.raw.ot_id}`,
      title: `${p.raw.client_name || "—"} · ${fmtHHMM(p.start)}–${fmtHHMM(p.end)} · ${p.raw.activity_type || ""}`,
      raw: p.raw,
    });
    // Buffer 30 min a la derecha
    const bufL = Math.min(WINDOW_MIN, b1);
    const bufR = Math.min(WINDOW_MIN, b1 + BUFFER_MIN);
    if (bufR > bufL) {
      blocks.push({
        type: "buffer",
        leftPct: (bufL / WINDOW_MIN) * 100,
        widthPct: ((bufR - bufL) / WINDOW_MIN) * 100,
        title: "Buffer de traslado 30 min",
      });
      usedMin += (bufR - bufL);
    }
  });
  const freeMin = Math.max(0, WINDOW_MIN - usedMin);
  return { blocks, freeMin };
}

function clampToWindow(dt, iso) {
  if (!dt) return null;
  // dt es Date local; ISO es 'YYYY-MM-DD' del día target en TZ Ecuador
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Guayaquil",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(dt).reduce((a, p) => { a[p.type] = p.value; return a; }, {});
  const dayISO = `${parts.year}-${parts.month}-${parts.day}`;
  const minutes = Number(parts.hour) * 60 + Number(parts.minute);
  if (dayISO < iso) return -1e6;
  if (dayISO > iso) return 1e6;
  return minutes - WINDOW_START_H * 60;
}

function fmtHHMM(dt) {
  return new Intl.DateTimeFormat("es-EC", { timeZone: "America/Guayaquil", hour: "2-digit", minute: "2-digit", hour12: false }).format(dt);
}

function shortName(n) {
  if (!n) return "—";
  const parts = n.trim().split(/\s+/);
  if (parts.length <= 2) return n;
  return `${parts[0]} ${parts[parts.length - 1]}`;
}
